import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';

// --- Types ---

export interface UploadedFile {
  fileName: string;
  fileType: string;
  fileSize: number;
  objectKey: string;
  publicUrl: string;
  widthPx: number | null;
  heightPx: number | null;
}

export interface ChatAttachment {
  id: string;
  message_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  object_key: string;
  public_url: string;
  width_px: number | null;
  height_px: number | null;
  created_at: string;
}

export interface PendingFile {
  id: string;
  file: File;
  preview: string | null;
  widthPx: number | null;
  heightPx: number | null;
}

// --- Helper: read file as base64 ---

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URL prefix (e.g. "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// --- Helper: get image dimensions ---

export function getImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  if (!file.type.startsWith('image/')) return Promise.resolve(null);

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

// --- Helper: create preview URL ---

export function createPreview(file: File): string | null {
  if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
    return URL.createObjectURL(file);
  }
  return null;
}

// --- Helper: format file size ---

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// --- Hook: upload files to R2 ---

export function useChatUpload() {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadFiles = useCallback(async (
    channelId: string,
    pendingFiles: PendingFile[]
  ): Promise<UploadedFile[]> => {
    setUploading(true);
    setUploadError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Convert files to base64 payloads
      const filePayloads = await Promise.all(
        pendingFiles.map(async (pf) => {
          const base64Data = await fileToBase64(pf.file);
          return {
            fileName: pf.file.name,
            fileType: pf.file.type || 'application/octet-stream',
            fileSize: pf.file.size,
            fileData: base64Data,
            widthPx: pf.widthPx,
            heightPx: pf.heightPx,
          };
        })
      );

      const response = await fetch('/.netlify/functions/r2-chat-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          channelId,
          files: filePayloads,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Upload failed (${response.status})`);
      }

      const result = await response.json();
      return result.files as UploadedFile[];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setUploadError(message);
      throw err;
    } finally {
      setUploading(false);
    }
  }, []);

  return { uploadFiles, uploading, uploadError, setUploadError };
}

// --- Hook: save attachments to DB after message is sent ---

export async function saveAttachments(
  messageId: string,
  uploads: UploadedFile[]
): Promise<ChatAttachment[]> {
  const rows = uploads.map((u) => ({
    message_id: messageId,
    file_name: u.fileName,
    file_type: u.fileType,
    file_size: u.fileSize,
    object_key: u.objectKey,
    public_url: u.publicUrl,
    width_px: u.widthPx,
    height_px: u.heightPx,
  }));

  const { data, error } = await supabase
    .from('chat_attachments')
    .insert(rows)
    .select();

  if (error) {
    console.error('Error saving attachments:', error);
    throw error;
  }

  return (data || []) as ChatAttachment[];
}

// --- Hook: fetch attachments for messages ---

// Cache to avoid re-fetching
const attachmentCache = new Map<string, ChatAttachment[]>();

export function useAttachments(messageIds: string[]) {
  const [attachments, setAttachments] = useState<Record<string, ChatAttachment[]>>({});
  const [loading, setLoading] = useState(false);
  const prevIdsRef = useRef<string>('');

  useEffect(() => {
    const idsKey = messageIds.sort().join(',');
    if (idsKey === prevIdsRef.current || messageIds.length === 0) return;
    prevIdsRef.current = idsKey;

    // Check which IDs need fetching
    const uncachedIds = messageIds.filter((id) => !attachmentCache.has(id));

    // Build result from cache
    const result: Record<string, ChatAttachment[]> = {};
    for (const id of messageIds) {
      const cached = attachmentCache.get(id);
      if (cached) {
        result[id] = cached;
      }
    }

    if (uncachedIds.length === 0) {
      setAttachments(result);
      return;
    }

    setLoading(true);

    supabase
      .from('chat_attachments')
      .select('*')
      .in('message_id', uncachedIds)
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        if (error) {
          console.error('Error fetching attachments:', error);
          setLoading(false);
          return;
        }

        // Group by message_id
        const grouped: Record<string, ChatAttachment[]> = {};
        for (const att of (data || []) as ChatAttachment[]) {
          if (!grouped[att.message_id]) {
            grouped[att.message_id] = [];
          }
          grouped[att.message_id].push(att);
        }

        // Cache results (including empty arrays for messages with no attachments)
        for (const id of uncachedIds) {
          attachmentCache.set(id, grouped[id] || []);
        }

        // Merge with cached results
        const merged = { ...result, ...grouped };
        // Also include empty arrays for uncached IDs that had no attachments
        for (const id of uncachedIds) {
          if (!merged[id]) {
            merged[id] = [];
          }
        }

        setAttachments(merged);
        setLoading(false);
      });
  }, [messageIds]);

  return { attachments, loading };
}

// Invalidate cache for a message (call after new attachments are saved)
export function invalidateAttachmentCache(messageId: string) {
  attachmentCache.delete(messageId);
}
